import React from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  KoobButton,
  KoobInputLabel,
  KoobInputNumber,
  KoobInputSelect,
  KoobInputToggle,
} from '@koob/ui';
import {
  EMPTY_DISTRIBUTION,
  hasOwnValue,
  isSet,
} from '../../../../utils/distributionInheritance';

// One level's TO values (KD-3896). An empty field inherits the value its
// placeholder shows, with a dashed border; an entered one is solid. On a
// period, free sale makes the allotment irrelevant; on a room or a season it
// only applies to the periods the DMC sells in free sale.
const DistributionFields = ({
  name,
  inherited,
  disabled,
  freeSaleDisabled,
  freeSaleLocksAllotment,
  label,
}) => {
  const { t } = useTranslation('toConnectionRequest');
  const [{ value }, meta, { setValue }] = useField(name);
  const own = value ?? EMPTY_DISTRIBUTION;
  const set = (patch) => setValue({ ...own, ...patch });

  const ownMargin = isSet(own.margin);
  // The DMC free sale caps the TO's, as the API resolves it.
  const freeSale =
    !freeSaleDisabled &&
    (isSet(own.freeSale) ? own.freeSale : inherited.freeSale);
  const typeLabel = (isPercent) =>
    isPercent === false
      ? t('bulkEdit.editor.marginFixed')
      : t('bulkEdit.editor.marginPercent');
  const allotmentError = meta.error?.allotmentCount;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-end justify-between gap-3">
        <div className="w-24 flex-none">
          <KoobInputLabel label={t('allotment')}>
            <KoobInputNumber
              size="sm"
              integer
              min={0}
              value={own.allotmentCount}
              placeholder={String(inherited.allotmentCount)}
              inherited={!isSet(own.allotmentCount)}
              error={Boolean(allotmentError)}
              disabled={disabled || (freeSaleLocksAllotment && freeSale)}
              onValueChange={(allotmentCount) => set({ allotmentCount })}
              aria-label={`${label} · ${t('allotment')}`}
            />
          </KoobInputLabel>
        </div>

        <div className="flex-none">
          <KoobInputLabel label={t('freeSale')}>
            <div className="flex h-8 items-center gap-1">
              <span
                className={`inline-flex rounded-full border p-0.5 ${
                  isSet(own.freeSale)
                    ? 'border-gray-400'
                    : 'border-dashed border-gray-300'
                }`}
              >
                <KoobInputToggle
                  size="sm"
                  checked={Boolean(freeSale)}
                  onCheckedChange={(checked) => set({ freeSale: checked })}
                  disabled={disabled || freeSaleDisabled}
                  aria-label={`${label} · ${t('freeSale')}`}
                />
              </span>
              {/* Kept when empty so the fields line up from one level to the next. */}
              <div className="w-7 flex-none">
                {isSet(own.freeSale) && !disabled && (
                  <KoobButton
                    variant="ghost"
                    size="icon"
                    type="button"
                    onClick={() => set({ freeSale: null })}
                    aria-label={`${label} · ${t('distribution.inheritFreeSale')}`}
                  >
                    <i className="fa-solid fa-xmark" />
                  </KoobButton>
                )}
              </div>
            </div>
          </KoobInputLabel>
        </div>

        <div className="w-28 flex-none">
          <KoobInputLabel label={t('margin')}>
            <KoobInputNumber
              size="sm"
              min={0}
              value={own.margin}
              placeholder={
                isSet(inherited.margin) ? String(inherited.margin) : ''
              }
              inherited={!ownMargin}
              disabled={disabled}
              onValueChange={(margin) =>
                set({
                  margin,
                  isMarginPercent:
                    margin === null
                      ? null
                      : (own.isMarginPercent ??
                        inherited.isMarginPercent ??
                        true),
                })
              }
              aria-label={`${label} · ${t('margin')}`}
            />
          </KoobInputLabel>
        </div>

        <div className="w-36 flex-none">
          <KoobInputSelect
            size="sm"
            label={t('bulkEdit.editor.marginType')}
            value={ownMargin ? own.isMarginPercent : ''}
            placeholder={typeLabel(inherited.isMarginPercent)}
            inherited={!ownMargin}
            disabled={disabled || !ownMargin}
            onValueChange={(isMarginPercent) => set({ isMarginPercent })}
            options={[
              { value: true, label: t('bulkEdit.editor.marginPercent') },
              { value: false, label: t('bulkEdit.editor.marginFixed') },
            ]}
          />
        </div>

        {/* Kept when empty so the fields line up from one level to the next. */}
        <div className="w-16 flex-none">
          {hasOwnValue(own) && !disabled && (
            <KoobButton
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => setValue({ ...EMPTY_DISTRIBUTION })}
            >
              {t('distribution.reset')}
            </KoobButton>
          )}
        </div>
      </div>

      {allotmentError && (
        <span className="text-xs text-red-600">{allotmentError}</span>
      )}
    </div>
  );
};

export default DistributionFields;
